import React, { useState, useEffect } from 'react';
import { ChevronLeft, Users, CreditCard, ExternalLink } from 'lucide-react';
import ApiService from '../services/ApiService';

const STATUS_LABELS = {
  'subscriptionStatus.paidActive': { label: 'Active', color: 'text-green-400' },
  'subscriptionStatus.trialActive': { label: 'Trial', color: 'text-blue-400' },
  'subscriptionStatus.paidExpired': { label: 'Expired', color: 'text-red-400' },
  'subscriptionStatus.trialExpired': { label: 'Trial Expired', color: 'text-red-400' },
  'subscriptionStatus.gracePeriod': { label: 'Grace Period', color: 'text-yellow-400' },
  'subscriptionStatus.billingRetry': { label: 'Payment Issue', color: 'text-yellow-400' },
  'subscriptionStatus.noSubscription': { label: 'No Subscription', color: 'text-gray-400' },
};

function SubscriptionView({ relatives = [], onBack, onShowToast, onOpenStoreFront }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [uncancelLoading, setUncancelLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const data = await ApiService.getSubscriptionInfo();
      setSubscription(data);
    } catch (error) {
      onShowToast?.('Failed to load subscription', 'error');
    }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const result = await ApiService.createStripePortal();
      if (result.portalUrl) {
        window.location.href = result.portalUrl;
      }
    } catch (error) {
      onShowToast?.(error.message || 'Failed to open billing portal', 'error');
    }
    setPortalLoading(false);
  };

  const handleReactivate = async () => {
    setUncancelLoading(true);
    try {
      await ApiService.uncancelSubscription();
      onShowToast?.('Subscription reactivated', 'success');
      await loadSubscription();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to reactivate subscription', 'error');
    }
    setUncancelLoading(false);
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await ApiService.cancelSubscription();
      onShowToast?.('Subscription will cancel at end of billing period', 'success');
      setShowCancelConfirm(false);
      await loadSubscription();
    } catch (error) {
      onShowToast?.(error.message || 'Failed to cancel subscription', 'error');
    }
    setCancelLoading(false);
  };

  const isCancelling = subscription?.autoRenewStatus === false
    && subscription?.subscriptionStatus === 'subscriptionStatus.paidActive';

  const statusInfo = isCancelling
    ? { label: 'Cancelling', color: 'text-yellow-400' }
    : (STATUS_LABELS[subscription?.subscriptionStatus] || { label: 'Unknown', color: 'text-gray-400' });
  const isTrialOrExpired = subscription?.subscriptionStatus?.includes('trial') || subscription?.subscriptionStatus?.includes('Expired') || subscription?.subscriptionStatus?.includes('noSubscription');
  const hasStripe = subscription?.paymentSource === 'stripe';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Subscription</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Current Plan */}
        <div className="bg-gray-800 rounded-xl p-5">
          <label className="text-sm text-gray-400 mb-3 block">Current Plan</label>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-white">
              {subscription?.planId === '0010' ? 'Free Trial' : `${subscription?.subscriptionFrequency === 'annual' ? 'Annual' : 'Monthly'} Plan`}
            </h2>
            <span className={`text-sm font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>

          {subscription?.price != null && subscription.price > 0 && (
            <p className="text-gray-400 text-sm">
              ${subscription.price.toFixed(2)} / {subscription.subscriptionFrequency === 'annual' ? 'year' : 'month'}
            </p>
          )}

          {subscription?.nextPaymentDate && (
            <p className="text-gray-500 text-sm mt-1">
              {subscription.autoRenewStatus ? 'Next payment' : 'Expires'}: {new Date(subscription.nextPaymentDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Members */}
        {subscription?.members && subscription.members.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-gray-400" />
              <label className="text-sm text-gray-400">
                Members ({subscription.members.length}{subscription.availableSlots != null ? ` / ${subscription.members.length + subscription.availableSlots}` : ''})
              </label>
            </div>
            <div className="space-y-2">
              {subscription.members.map(member => (
                <div key={member.uuid} className="flex items-center justify-between py-1.5">
                  <span className="text-white text-sm">{relatives.find(r => r.uuid === member.uuid)?.name || member.uuid}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    member.subscriptionRole === 'owner'
                      ? 'bg-purple-600/20 text-purple-400'
                      : member.invitationStatus === 'accepted'
                        ? 'bg-green-600/20 text-green-400'
                        : 'bg-yellow-600/20 text-yellow-400'
                  }`}>
                    {member.subscriptionRole === 'owner' ? 'Owner' : member.invitationStatus}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Reactivate (when cancelled but still active) */}
          {isCancelling ? (
            <button
              onClick={handleReactivate}
              disabled={uncancelLoading}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {uncancelLoading ? 'Reactivating...' : 'Reactivate Subscription'}
            </button>
          ) : (
            /* Change Plan */
            <button
              onClick={onOpenStoreFront}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white text-sm font-medium transition-colors"
            >
              {isTrialOrExpired ? 'Subscribe Now' : 'Change Plan'}
            </button>
          )}

          {/* Manage Billing (Stripe portal) */}
          {hasStripe && (
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="flex items-center justify-center gap-2 w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              <CreditCard className="w-4 h-4" />
              {portalLoading ? 'Opening...' : 'Manage Billing'}
              <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}

          {/* Cancel Subscription */}
          {hasStripe && !isCancelling && !isTrialOrExpired && (
            <>
              {showCancelConfirm ? (
                <div className="bg-gray-800 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-gray-300">Your subscription will remain active until the end of the current billing period. Are you sure?</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancel}
                      disabled={cancelLoading}
                      className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-red-400 text-sm font-medium transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubscriptionView;
